import Anchor from "@components/controls/Anchor";
import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import t from "@ext/localization/locale/translate";
import getRenderRows from "@ext/markdown/elements/view/render/logic/getRenderRows";
import type { Property, ViewRenderGroup } from "@ext/properties/models";
import { type ElementType, useMemo } from "react";

const Wrapper = ({
	children,
	basePath,
	catalogName,
	itemPath,
}: {
	children: JSX.Element;
	basePath: string;
	catalogName: string;
	itemPath: string;
}) => {
	if (!itemPath) return children;
	const apiUrlCreator = new ApiUrlCreator(basePath, catalogName, itemPath);

	return (
		<ApiUrlCreatorService.Provider value={apiUrlCreator}>
			<ResourceService.Provider>{children}</ResourceService.Provider>
		</ApiUrlCreatorService.Provider>
	);
};

const Group = ({
	group,
	select,
	groupCount,
	catalogName,
	catalogProperties,
	Row,
	Cell,
	firstColumnClassName,
}: {
	group: ViewRenderGroup;
	select: string[];
	groupCount: number;
	catalogName: string;
	catalogProperties: Map<string, Property>;
	Row: ElementType;
	Cell: ElementType;
	firstColumnClassName?: string;
}) => {
	const router = useRouter();
	const rows = useMemo(
		() => getRenderRows(group, select, catalogProperties, groupCount),
		[group, select, catalogProperties, groupCount],
	);

	return (
		<>
			{rows.map((row, index) => (
				<Row
					key={`row-${row.length}${
						// biome-ignore lint/suspicious/noArrayIndexKey: expected
						index
					}`}
				>
					{row.map((cell, cellIndex) => {
						return (
							<Wrapper
								basePath={router.basePath}
								catalogName={catalogName}
								itemPath={cell?.itemPath}
								key={`cell-${cell?.name !== undefined ? cell.name : cell?.article?.title}-${
									// biome-ignore lint/suspicious/noArrayIndexKey: expected
									cellIndex
								}`}
							>
								<Cell
									className={cell?.column === 0 ? firstColumnClassName : undefined}
									rowSpan={cell?.rowSpan ?? 1}
									style={{ width: cell?.width }}
								>
									{cell?.name}
									{cell.article && (
										<Anchor href={cell.article.linkPath} resourcePath={cell.article.resourcePath}>
											{cell.article.title || t("article.no-name")}
										</Anchor>
									)}
								</Cell>
							</Wrapper>
						);
					})}
				</Row>
			))}
		</>
	);
};

export default Group;
